"use client";

import { useEffect, useState } from "react";
import { runtimeConfig } from "../../src/runtimeConfig";
import { detectDownloadPlatform, type DownloadPlatform } from "./platform";

type DetectionState = "detecting" | DownloadPlatform;
type ReleaseState =
  | { status: "checking" }
  | { status: "unavailable" }
  | { status: "available"; version: string; sha256: string | null; downloadUrl: string };

const windowsSteps = [
  <>Download and run the GeoStream <code>.exe</code> installer.</>,
  <>Confirm the publisher and installer name before continuing.</>,
  <>Launch GeoStream from the Start menu.</>,
  <>Approve camera and microphone access, then run Preflight.</>,
];

const macosSteps = [
  <>Open the signed and notarized GeoStream <code>.dmg</code>.</>,
  <>Drag GeoStream into the Applications folder.</>,
  <>Eject the disk image and open GeoStream from Applications.</>,
  <>Approve camera, microphone, and screen-recording access, then run Preflight.</>,
];

export function DeviceDownload() {
  const [platform, setPlatform] = useState<DetectionState>("detecting");
  const [release, setRelease] = useState<ReleaseState>({ status: "checking" });

  useEffect(() => {
    let active = true;
    async function detectAndCheckRelease() {
      let detected: DownloadPlatform = "unsupported";
      try {
        detected = await detectDownloadPlatform(window.navigator);
        if (!active) return;
        setPlatform(detected);
        if (detected === "unsupported") {
          setRelease({ status: "unavailable" });
          return;
        }
        const apiBaseUrl = runtimeConfig().apiBaseUrl;
        if (!apiBaseUrl) {
          setRelease({ status: "unavailable" });
          return;
        }
        const response = await fetch(`${apiBaseUrl}/api/v1/releases/${detected}`, {
          headers: { Accept: "application/json" },
        });
        if (!active) return;
        if (!response.ok) {
          setRelease({ status: "unavailable" });
          return;
        }
        const candidate = await response.json() as Partial<{ platform: string; version: string; sha256: string; downloadUrl: string }>;
        if (!active) return;
        const expectedDownloadUrl = `/api/v1/releases/${detected}/download`;
        if (candidate.platform !== detected || typeof candidate.version !== "string" || candidate.downloadUrl !== expectedDownloadUrl) {
          setRelease({ status: "unavailable" });
          return;
        }
        setRelease({
          status: "available",
          version: candidate.version,
          sha256: typeof candidate.sha256 === "string" && /^[a-f0-9]{64}$/i.test(candidate.sha256) ? candidate.sha256 : null,
          downloadUrl: `${apiBaseUrl}${expectedDownloadUrl}`,
        });
      } catch {
        if (active) {
          setPlatform(detected);
          setRelease({ status: "unavailable" });
        }
      }
    }
    void detectAndCheckRelease();
    return () => { active = false; };
  }, []);

  if (platform === "detecting") {
    return (
      <section className="device-detection" aria-live="polite" aria-busy="true">
        <span className="device-spinner" aria-hidden="true" />
        <div><p className="eyebrow">Safe download</p><h2>Detecting your computer…</h2><p>GeoStream will show only the installer that matches this device.</p></div>
      </section>
    );
  }

  if (platform === "unsupported") {
    return (
      <section className="device-detection device-unsupported" aria-live="polite">
        <span className="platform-symbol">!</span>
        <div><p className="eyebrow">No compatible installer</p><h2>This device is not currently supported.</h2><p>GeoStream Beta currently supports 64-bit Windows 10/11 computers and macOS 13 or newer. Visit this page from the computer where you plan to install GeoStream.</p></div>
      </section>
    );
  }

  const windows = platform === "windows";
  const steps = windows ? windowsSteps : macosSteps;
  const downloadAvailable = release.status === "available";
  return (
    <>
      <section className="download-grid download-grid-single" aria-live="polite">
        <article className="download-card">
          <div className="platform-heading">
            <span className="platform-symbol">{windows ? "⊞" : "●"}</span>
            <div><p className="eyebrow">{windows ? "Windows detected" : "macOS detected"}</p><h2>{windows ? "Windows 10 / 11" : "macOS 13 or newer"}</h2></div>
          </div>
          <p>{windows
            ? <>For 64-bit Intel and AMD computers. GeoStream selected the standard <code>.exe</code> installer for this device.</>
            : <>The universal GeoStream <code>.dmg</code> supports both Apple silicon and Intel Macs, so there is no processor choice to get wrong.</>}</p>
          <div className="download-meta">
            <span><b>Architecture</b>{windows ? "x64" : "Universal"}</span>
            <span><b>Package</b>{windows ? "EXE" : "DMG"}</span>
            <span><b>Security</b>{windows ? "Signature required" : "Signed + notarized"}</span>
          </div>
          {downloadAvailable
            ? <a className="button button-primary button-full" href={release.downloadUrl}>Download GeoStream for {windows ? "Windows" : "macOS"}</a>
            : <span className="button button-disabled button-full" aria-disabled="true">{release.status === "checking" ? "Checking signed package…" : `Signed ${windows ? "Windows" : "macOS"} beta is being finalized`}</span>}
          <small>GeoStream automatically selected this installer from your device. Other platform packages are intentionally hidden.{!downloadAvailable && release.status !== "checking" && " The download will activate after the signed package passes clean-install testing."}</small>
          {downloadAvailable && <small className="download-integrity">Version {release.version}{release.sha256 && <> · SHA-256 <code>{release.sha256}</code></>}</small>}
        </article>
      </section>

      <section className="detected-install-card">
        <span className="install-number">{windows ? "WINDOWS INSTALL" : "MACOS INSTALL"}</span>
        <h3>{windows ? "Install with the EXE" : "Install from the DMG"}</h3>
        <ol>{steps.map((step, index) => <li key={index}>{step}</li>)}</ol>
      </section>
    </>
  );
}
